import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { io, Socket } from "socket.io-client";

import { RangeType, useAsu } from "@framework/provider-asu";
import { IUserDto, SelfRecorderMode, useUser } from "@framework/provider-user";

import { ISubscribeContext, ISubscribeRequest, SubscribeCode, SubscribeListener, SubscribeMessage } from "./types";

interface ISubscriptionEntry extends ISubscribeRequest {
  listeners: Set<SubscribeListener>;
  queue: SubscribeMessage[];
}

const SubscribeContext = createContext<ISubscribeContext>({
  connected: false,
  enabled: false,
  viewportEnd: new Date(),
  subscribe: () => () => undefined,
});

const getSubscriptionKey = ({ type, id }: ISubscribeRequest): string => `${type}:${id}`;

const getMessageRequest = (message: SubscribeMessage): ISubscribeRequest | null => {
  if ("idMeasure" in message) {
    return { type: message.SubscribeType, id: message.idMeasure };
  }
  if ("ObjectId" in message) {
    return { type: message.SubscribeType, id: message.ObjectId };
  }
  if ("idTrain" in message) {
    return { type: SubscribeCode.PARAM, id: message.idTrain };
  }
  return null;
};

const getSocketOrigin = (): string => {
  const configuredUrl = process.env.BE_URL;
  if (!configuredUrl) {
    return window.location.origin;
  }

  try {
    return new URL(configuredUrl, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
};

export const SubscribeProvider: FC<PropsWithChildren> = ({ children }) => {
  const {
    endTimestamp,
    isMenuOpen,
    isRecorderModeActive,
    setSubscribeSignalingActive,
    startTimestamp,
    updateList,
    userSelectedRangeType,
  } = useAsu();
  const user = useUser<IUserDto>();
  const mode = user.profile?.selfRecorderSettings?.mode ?? SelfRecorderMode.INTERVAL;
  const enabled =
    isRecorderModeActive && userSelectedRangeType === RangeType.RECORDER_MODE && mode === SelfRecorderMode.SUBSCRIBE;

  const socketRef = useRef<Socket | null>(null);
  const entriesRef = useRef(new Map<string, ISubscriptionEntry>());
  const menuSignalingLiveRef = useRef(false);
  const [connected, setConnected] = useState(false);
  const [viewportEnd, setViewportEnd] = useState(() => new Date());

  const emitRequest = useCallback((event: "subscribe" | "unsubscribe", request: ISubscribeRequest): void => {
    const socket = socketRef.current;
    if (!socket?.connected) {
      return;
    }
    socket.emit(event, JSON.stringify(request));
  }, []);

  const subscribe = useCallback(
    (request: ISubscribeRequest, listener: SubscribeListener): (() => void) => {
      const key = getSubscriptionKey(request);
      let entry = entriesRef.current.get(key);

      if (!entry) {
        entry = { ...request, listeners: new Set(), queue: [] };
        entriesRef.current.set(key, entry);
        emitRequest("subscribe", request);
      }
      entry.listeners.add(listener);

      return () => {
        const currentEntry = entriesRef.current.get(key);
        if (!currentEntry) {
          return;
        }
        currentEntry.listeners.delete(listener);
        if (currentEntry.listeners.size === 0) {
          entriesRef.current.delete(key);
          emitRequest("unsubscribe", request);
        }
      };
    },
    [emitRequest],
  );

  useEffect(() => {
    if (!enabled) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      setConnected(false);
      return;
    }

    const socket = io(`${getSocketOrigin()}/api/subscribe-mode`, {
      withCredentials: true,
      transports: ["websocket", "polling"],
    });
    socketRef.current = socket;

    const handleConnect = (): void => {
      setConnected(true);
      for (const entry of entriesRef.current.values()) {
        socket.emit("subscribe", JSON.stringify({ id: entry.id, type: entry.type }));
      }
    };
    const handleDisconnect = (): void => setConnected(false);
    const handleMessage = (message: SubscribeMessage): void => {
      const request = getMessageRequest(message);
      if (!request) {
        return;
      }
      entriesRef.current.get(getSubscriptionKey(request))?.queue.push(message);
    };
    const handleError = (): void => setConnected(false);

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("subscribe", handleMessage);
    socket.on("error", handleError);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("subscribe", handleMessage);
      socket.off("error", handleError);
      socket.disconnect();
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
      setConnected(false);
    };
  }, [enabled]);

  useEffect(() => {
    setSubscribeSignalingActive(enabled && connected);

    return () => {
      setSubscribeSignalingActive(false);
    };
  }, [connected, enabled, setSubscribeSignalingActive]);

  useEffect(() => {
    const menuSignalingLive = enabled && connected && isMenuOpen;

    if (menuSignalingLive && !menuSignalingLiveRef.current) {
      void updateList({
        startTimestamp,
        endTimestamp,
        signalingOnly: true,
      });
    }

    menuSignalingLiveRef.current = menuSignalingLive;
  }, [connected, enabled, endTimestamp, isMenuOpen, startTimestamp, updateList]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const flush = (): void => {
      const nextViewportEnd = new Date();
      setViewportEnd(nextViewportEnd);

      for (const entry of entriesRef.current.values()) {
        const messages = entry.queue.splice(0);
        for (const listener of entry.listeners) {
          listener(messages, nextViewportEnd);
        }
      }
    };

    const timer = window.setInterval(flush, 1_000);
    return () => window.clearInterval(timer);
  }, [enabled]);

  const value = useMemo<ISubscribeContext>(
    () => ({ connected, enabled, viewportEnd, subscribe }),
    [connected, enabled, subscribe, viewportEnd],
  );

  return <SubscribeContext.Provider value={value}>{children}</SubscribeContext.Provider>;
};

export const useSubscribe = (): ISubscribeContext => useContext(SubscribeContext);
